import {
  typedCollection,
  typedDoc,
  getCollection,
  getDocument,
  where,
  orderBy,
  limit,
} from '@/lib/firebase/firestore'
import {
  createCallable,
  functions,
  getOrderReceiptFn,
  getPublicTicketAvailabilityFn,
  requestTicketOrdersByEmailFn,
} from '@/lib/firebase/functions'
import { createUserFacingError } from '@/lib/errors'
import type { TicketOrderReceiptResponse } from '@/lib/firebase/functions'
import type {
  EventDocument,
  TicketTypeDocument,
  TicketOrderDocument,
  TicketDocument,
} from '@/types'
import type {
  CurrentOwnerOrder,
  CurrentOwnerTicket,
  CurrentOwnerTicketResult,
  GoogleWalletPassResult,
  TicketTransferClaimResult,
  TicketTransferView,
} from '../types'

interface OwnerAccessInput {
  /** Required for guest management. Never replace this with an email address. */
  ownerAccessToken?: string | null
}

interface CreateTicketTransferInput extends OwnerAccessInput {
  ticketId: string
  recipientEmail: string
}

interface ManageTicketTransferInput extends OwnerAccessInput {
  transferId: string
}

interface CreateTicketTransferBackendResponse {
  transferId: string
  status: 'pending'
  expiresAt: string
  emailSent: boolean
}

interface ClaimTicketTransferBackendResponse {
  ticketId: string
  status: 'claimed'
  currentOwnerEmail: string
  currentOwnerUid: string | null
  qrVersion: number
  ownerVersion: number
  managementUrl: string
}

interface CancelTicketTransferBackendResponse {
  ok: true
  status: 'cancelled' | 'expired'
}

interface ResendTicketTransferBackendResponse {
  transferId: string
  status: 'pending'
  expiresAt: string
  emailSent: boolean
}

interface TicketManagementPendingTransferBackend {
  transferId: string
  status: 'pending'
  recipientEmail: string
  expiresAt: string
}

interface TicketManagementContextBackendResponse {
  ticketId: string
  eventId: string
  eventTitle: string
  eventDate: string | null
  eventVenue: string
  ticketTypeId: string
  ticketTypeName: string
  orderId: string | null
  source: string | null
  status: string
  checkedIn: boolean
  checkedInAt: string | null
  currentOwnerUid: string | null
  currentOwnerEmail: string | null
  qrToken: string
  qrImageDataUrl?: string
  qrVersion: number
  ownerVersion: number
  ownerAccessVersion: number
  canTransfer: boolean
  pendingTransfer: TicketManagementPendingTransferBackend | null
}

interface GoogleWalletPassBackendResponse {
  available: boolean
  saveUrl?: string
  reason?: 'not_configured' | 'invalid_ticket' | 'provider_error'
}

const createTicketTransferFn = createCallable<
  CreateTicketTransferInput,
  CreateTicketTransferBackendResponse
>(functions, 'createTicketTransfer')

const claimTicketTransferFn = createCallable<
  { transferId: string; token: string },
  ClaimTicketTransferBackendResponse
>(functions, 'claimTicketTransfer')

const cancelTicketTransferFn = createCallable<
  ManageTicketTransferInput,
  CancelTicketTransferBackendResponse
>(functions, 'cancelTicketTransfer')

const resendTicketTransferFn = createCallable<
  ManageTicketTransferInput,
  ResendTicketTransferBackendResponse
>(functions, 'resendTicketTransfer')

/**
 * Expected backend contract for secure guest ticket management. This callable
 * is intentionally not replaced with a Firestore email query or ownerEmail
 * argument: a guest URL must carry a high-entropy ownerAccessToken.
 */
const getTicketManagementContextFn = createCallable<
  { ticketId: string; ownerAccessToken?: string },
  TicketManagementContextBackendResponse
>(functions, 'getTicketManagementContext')

/**
 * Expected backend contract for Wallet issuance. The server must re-read the
 * ticket and sign the pass; the browser never receives QR authority data.
 */
const createGoogleWalletPassFn = createCallable<
  OwnerAccessInput & { ticketId: string },
  GoogleWalletPassBackendResponse
>(functions, 'createGoogleWalletPass')

const eventsCol = () => typedCollection<EventDocument>('events')
const ticketTypesCol = (eventId: string) =>
  typedCollection<TicketTypeDocument>('events/' + eventId + '/ticketTypes')
const ordersCol = () => typedCollection<TicketOrderDocument>('orders')
const ticketsCol = () => typedCollection<TicketDocument>('tickets')

function normalizeEmail(value: string | null | undefined): string | null {
  if (!value) return null
  const normalized = value.trim().toLowerCase()
  return normalized.includes('@') ? normalized : null
}

function normalizeOwnerAccessToken(value: string | null | undefined): string | undefined {
  const token = value?.trim()
  return token || undefined
}

function ticketEventDateMillis(value: CurrentOwnerTicket['eventDate']): number {
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return value?.toMillis?.() ?? 0
}

function dedupeTickets(tickets: CurrentOwnerTicket[]): CurrentOwnerTicket[] {
  const byId = new Map<string, CurrentOwnerTicket>()
  for (const ticket of tickets) {
    if (ticket.id) byId.set(ticket.id, ticket)
  }
  return Array.from(byId.values()).sort((left, right) => {
    const leftDate = ticketEventDateMillis(left.eventDate)
    const rightDate = ticketEventDateMillis(right.eventDate)
    return leftDate - rightDate || left.eventTitle.localeCompare(right.eventTitle)
  })
}

function mapTicketManagementContext(
  context: TicketManagementContextBackendResponse,
): CurrentOwnerTicket {
  return {
    id: context.ticketId,
    eventId: context.eventId,
    eventTitle: context.eventTitle,
    eventDate: context.eventDate as unknown as CurrentOwnerTicket['eventDate'],
    eventVenue: context.eventVenue,
    ticketTypeId: context.ticketTypeId,
    ticketTypeName: context.ticketTypeName,
    orderId: context.orderId ?? '',
    purchaserId: context.currentOwnerUid,
    purchaserEmail: context.currentOwnerEmail ?? '',
    purchaserName: '',
    qrToken: context.qrToken,
    qrImageUrl: context.qrImageDataUrl,
    checkedIn: context.checkedIn,
    checkedInAt: null,
    checkedInBy: null,
    status: context.status as CurrentOwnerTicket['status'],
    source: context.source as CurrentOwnerTicket['source'],
    currentOwnerId: context.currentOwnerUid,
    currentOwnerUid: context.currentOwnerUid,
    currentOwnerEmail: context.currentOwnerEmail ?? undefined,
    currentOwnerEmailNormalized: context.currentOwnerEmail ?? undefined,
    ownerVersion: context.ownerVersion,
    ownershipVersion: context.ownerVersion,
    qrVersion: context.qrVersion,
    pendingTransferId: context.pendingTransfer?.transferId ?? null,
    pendingTransfer: context.pendingTransfer,
    canTransfer: context.canTransfer,
  }
}

async function loadOrdersForTickets(
  tickets: CurrentOwnerTicket[],
): Promise<CurrentOwnerOrder[]> {
  // Order records belong to the original purchaser and can include their
  // contact/payment metadata. A transferred owner receives only the ticket.
  const orderIds = [...new Set(
    tickets
      .filter((ticket) => (ticket.ownerVersion ?? ticket.ownershipVersion ?? 1) <= 1)
      .map((ticket) => ticket.orderId)
      .filter((orderId): orderId is string => typeof orderId === 'string' && orderId.length > 0),
  )]
  const results = await Promise.allSettled(
    orderIds.map(async (orderId) => {
      const order = await getDocument(typedDoc<TicketOrderDocument>('orders', orderId))
      return order ? ({ ...order, id: orderId } as CurrentOwnerOrder) : null
    }),
  )
  return results.flatMap((result) => (result.status === 'fulfilled' && result.value ? [result.value] : []))
}

export const ticketingService = {
  async getPublishedEvents(): Promise<Array<EventDocument & { id: string }>> {
    const events = await getCollection(
      eventsCol(),
      where('isPublished', '==', true),
      orderBy('eventDate', 'asc'),
    )
    // Legacy public events predate this flag. An explicit false is used only
    // while a participant roster is being resolved; ticket-type rules and the
    // server checkout transaction enforce the same gate authoritatively.
    return events.filter((event) =>
      event.productionId
        ? event.publicSalesEnabled === true
        : event.publicSalesEnabled !== false,
    )
  },

  async getEventBySlug(slug: string): Promise<(EventDocument & { id: string }) | null> {
    const results = await getCollection(
      eventsCol(),
      where('slug', '==', slug),
      where('isPublished', '==', true),
      limit(1),
    )
    return results[0] ?? null
  },

  async getTicketTypes(eventId: string): Promise<Array<TicketTypeDocument & { id: string }>> {
    return getCollection(
      ticketTypesCol(eventId),
      where('isVisible', '==', true),
      orderBy('order', 'asc'),
    )
  },

  async getPublicRemainingSeats(eventId: string): Promise<number | null> {
    const result = await getPublicTicketAvailabilityFn({ eventId })
    return result.data.remainingSharedSeats
  },

  async requestOrdersByEmail(email: string): Promise<void> {
    await requestTicketOrdersByEmailFn({ email })
  },

  async getOrdersByUserId(userId: string): Promise<Array<TicketOrderDocument & { id: string }>> {
    return getCollection(
      ordersCol(),
      where('purchaserId', '==', userId),
      where('status', '==', 'paid'),
    )
  },

  async getTicketsByOrderId(orderId: string): Promise<Array<TicketDocument & { id: string }>> {
    return getCollection(ticketsCol(), where('orderId', '==', orderId))
  },

  /**
   * Loads the tickets currently owned by the caller. Original purchaser/order
   * ownership is deliberately not used here, so claimed tickets follow their
   * current owner.
   */
  async getCurrentOwnerTickets(params: {
    uid?: string | null
    email?: string | null
    ticketId?: string | null
    ownerAccessToken?: string | null
  }): Promise<CurrentOwnerTicketResult> {
    const ownerAccessToken = normalizeOwnerAccessToken(params.ownerAccessToken)
    if (ownerAccessToken) {
      if (!params.ticketId?.trim()) {
        throw createUserFacingError(
          'This secure ticket link is missing its ticket reference.',
          'ticketing/missing-ticket-reference',
        )
      }
      const response = await getTicketManagementContextFn({
        ticketId: params.ticketId.trim(),
        ownerAccessToken,
      })
      const tickets = dedupeTickets([mapTicketManagementContext(response.data)])
      const orders = await loadOrdersForTickets(tickets)
      return { tickets, orders }
    }

    const uid = params.uid?.trim() || null
    const email = normalizeEmail(params.email)
    if (!uid && !email) return { tickets: [], orders: [] }

    // Query every supported ownership alias. During migration, Firestore rules
    // or indexes may reject one alias; a successful legacy alias must still let
    // a transferred owner see their ticket. The final error is surfaced only
    // when every applicable query failed.
    const queries = [
      ...(uid
        ? [
            getCollection(ticketsCol(), where('currentOwnerUid', '==', uid)),
            getCollection(ticketsCol(), where('currentOwnerId', '==', uid)),
            getCollection(ticketsCol(), where('purchaserId', '==', uid)),
          ]
        : []),
      ...(email
        ? [
            getCollection(ticketsCol(), where('currentOwnerEmailNormalized', '==', email)),
            getCollection(ticketsCol(), where('currentOwnerEmail', '==', email)),
            getCollection(ticketsCol(), where('purchaserEmailNormalized', '==', email)),
            getCollection(ticketsCol(), where('purchaserEmail', '==', email)),
          ]
        : []),
    ]
    const results = await Promise.allSettled(queries)
    const successful = results.filter(
      (result): result is PromiseFulfilledResult<Array<TicketDocument & { id: string }>> =>
        result.status === 'fulfilled',
    )
    if (successful.length === 0 && results.length > 0) {
      const firstFailure = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      )
      throw firstFailure?.reason ?? createUserFacingError(
        'We could not load your tickets. Please try again.',
        'ticketing/load-current-owner-tickets',
      )
    }

    const listedTickets = dedupeTickets(
      successful.flatMap((result) => result.value as CurrentOwnerTicket[]),
    )
    // Signed-in UID owners use the same callable projection as secure-link
    // guests so every ticket receives a server-rendered QR image and current
    // transfer state. A legacy email-only ticket remains listed if it cannot
    // yet be upgraded to UID ownership; the owner can request a secure link.
    const hydrated = await Promise.allSettled(
      listedTickets.map(async (ticket) => {
        const response = await getTicketManagementContextFn({ ticketId: ticket.id })
        return mapTicketManagementContext(response.data)
      }),
    )
    const tickets = dedupeTickets(
      hydrated.map((result, index) =>
        result.status === 'fulfilled' ? result.value : listedTickets[index]!,
      ),
    )
    return { tickets, orders: await loadOrdersForTickets(tickets) }
  },

  async createTicketTransfer(
    input: CreateTicketTransferInput,
  ): Promise<TicketTransferView> {
    const ownerAccessToken = normalizeOwnerAccessToken(input.ownerAccessToken)
    const response = await createTicketTransferFn({
      ticketId: input.ticketId,
      recipientEmail: input.recipientEmail.trim(),
      ...(ownerAccessToken ? { ownerAccessToken } : {}),
    })
    return { ...response.data }
  },

  async claimTicketTransfer(
    transferId: string,
    token: string,
  ): Promise<TicketTransferClaimResult> {
    const response = await claimTicketTransferFn({ transferId, token })
    return response.data
  },

  async cancelTicketTransfer(input: ManageTicketTransferInput): Promise<CancelTicketTransferBackendResponse> {
    const ownerAccessToken = normalizeOwnerAccessToken(input.ownerAccessToken)
    const response = await cancelTicketTransferFn({
      transferId: input.transferId,
      ...(ownerAccessToken ? { ownerAccessToken } : {}),
    })
    return response.data
  },

  async resendTicketTransfer(
    input: ManageTicketTransferInput,
  ): Promise<TicketTransferView> {
    const ownerAccessToken = normalizeOwnerAccessToken(input.ownerAccessToken)
    const response = await resendTicketTransferFn({
      transferId: input.transferId,
      ...(ownerAccessToken ? { ownerAccessToken } : {}),
    })
    return { ...response.data }
  },

  async createGoogleWalletPass(input: {
    ticketId: string
    ownerAccessToken?: string | null
  }): Promise<GoogleWalletPassResult> {
    const ownerAccessToken = normalizeOwnerAccessToken(input.ownerAccessToken)
    const response = await createGoogleWalletPassFn({
      ticketId: input.ticketId,
      ...(ownerAccessToken ? { ownerAccessToken } : {}),
    })
    const saveUrl = response.data.saveUrl
    const hasSafeSaveUrl =
      typeof saveUrl === 'string' &&
      saveUrl.startsWith('https://pay.google.com/gp/v/save/')
    return {
      available: response.data.available === true && hasSafeSaveUrl,
      ...(hasSafeSaveUrl ? { saveUrl } : {}),
      ...(response.data.reason ? { reason: response.data.reason } : {}),
    }
  },

  async getOrderReceipt(orderId: string, receiptToken: string): Promise<TicketOrderReceiptResponse> {
    const response = await getOrderReceiptFn({ kind: 'ticket', orderId, receiptToken })
    if (response.data.kind !== 'ticket') {
      throw new Error('Unexpected receipt type.')
    }
    return response.data
  },
}
